import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { createAppointment, updateAppointment, deleteAppointment, getAppointment, splitAppointment } from '../../logic/calendar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Col, Row, Container, Form, ButtonGroup, Button } from 'react-bootstrap';
import access from 'safe-access';
import map from './../../assets/images/map.png';
import moment from 'moment';
import { camel } from './../../libs/general';
import 'react-phone-input-2/lib/style.css';
import {countryCodeToRegionCodeMap} from './../../config';

function AppointmentDetails() {
	const params = useParams();
	const location = useLocation();
	const navigate = useNavigate();
	
	const [state, setState] = useState({
		item: {
			fromTimestamp: '',
			toTimestamp: '',
			type: 'available',
			patient: {
				name: {
					firstName: '',
					lastName: ''
				},
				email: '',
				mobile: {
					countryPrefixCode: '',
					mobileNumber: ''
				},
				landline: {
					countryPrefixCode: '',
					landlineNumber: ''
				},
				idCard: '',
				dob: '',
				remindManually: "true"
			},
			clinic: '',
			pharmacyRemarks: '',
			doctorRemarks: '',
			smsReminder: 'NEVER SENT',
			emailReminder: 'NEVER SENT',
			billing: {
				doctorRate: '',
				clinicFee: '',
				toBePaidAt: '',
				services: []
			}
		},
		editMode: false,
		remarksAmount: 0,
		billingAmount: 0,
		currentView: 'general',
		fields: '',
		error: ''
	});

	useEffect(() => {
		async function setupComponent() {
			let fromTimestamp = '', toTimestamp = '';

			if (access(location, 'state.refreshRequired') === true) {
				const appointment = await getAppointment(window.localStorage.getItem('currentDoctorId'), location.state.item.creationTimestamp);
				location.state.item = appointment;
			}

			if (access(location, 'state.item.type') !== 'pending' && access(location, 'state.item.fromTimestamp')) {
				fromTimestamp = location.state.item.fromTimestamp;
				window.localStorage.setItem('tempFromTimestamp', fromTimestamp);
			} else {
				fromTimestamp = window.localStorage.getItem('tempFromTimestamp');
				window.localStorage.removeItem('tempFromTimestamp');
			}

			if (access(location, 'state.item.type') !== 'pending' && access(location, 'state.item.toTimestamp')) {
				toTimestamp = location.state.item.toTimestamp;
				window.localStorage.setItem('tempToTimestamp', toTimestamp);
			} else {
				toTimestamp = window.localStorage.getItem('tempToTimestamp');
				window.localStorage.removeItem('tempToTimestamp');
			}

			if (access(location, 'state.item.type')) location.state.item.type = "filled";

			if (location && location.state && location.state.item && location.state.item.patient) {
				const { item } = location.state;
				item.fromTimestamp = fromTimestamp;
				item.toTimestamp = toTimestamp;

				let remarksAmount = 0;
				let billingAmount = 0;
				if (item && item.reminders) {
					if (item.reminders.email) {
						switch (item.reminders.email.lastStatus) {
							case "SENT":
								item.emailReminder = 'SENT';
								if (item.reminders.email.lastSentTimestamp)
									item.emailReminder += ` on ${moment(item.reminders.email.lastSentTimestamp).format('DD MMM YYYY HH:mm')}`;
								break;
							case "ERROR":
								item.emailReminder = 'ERROR OCCURED WHEN SENDING';
								break;
							default:
								item.emailReminder = 'NEVER SENT';
						}
					} else item.emailReminder = 'NEVER SENT';
					if (item.reminders.sms) {
						switch (item.reminders.sms.lastStatus) {
							case "SENT":
								item.smsReminder = 'SENT';
								if (item.reminders.sms.lastSentTimestamp)
									item.smsReminder += ` on ${moment(item.reminders.sms.lastSentTimestamp).format('DD MMM YYYY HH:mm')}`;
								break;
							case "ERROR":
								item.smsReminder = 'ERROR OCCURED WHEN SENDING';
								break;
							default:
								item.smsReminder = 'NEVER SENT';
						}
					} else item.smsReminder = 'NEVER SENT';
				} else {
					item.emailReminder = 'NEVER SENT';
					item.smsReminder = 'NEVER SENT';
				}
				if (item && item.pharmacyRemarks && item.pharmacyRemarks.length > 0) remarksAmount++;
				if (item && item.doctorRemarks && item.doctorRemarks.length > 0) remarksAmount++;
				if (item && item.billing && item.billing.services && item.billing.services.length > 0)
					billingAmount = item.billing.services.filter((service) => service.bought === true).length;

				let fields = null;
				try {
					fields = JSON.parse(window.localStorage.getItem('currentDoctorFields'));
				} catch (e) {
					fields = null;
				}
				setState(prevState => ({ ...prevState, item, editMode: true, remarksAmount, billingAmount, fields }));
			} else {
				const newState = { ...state };
				newState.item.fromTimestamp = fromTimestamp;
				newState.item.toTimestamp = toTimestamp;
				newState.item.clinic = window.localStorage.getItem('currentDoctorClinic');
				newState.item.billing.doctorRate = window.localStorage.getItem('currentDoctorRate');
				newState.item.billing.clinicFee = window.localStorage.getItem('currentDoctorClinicFee');
				newState.item.billing.toBePaidAt = window.localStorage.getItem('currentDoctorPayAt');

				try {
					newState.item.billing.services = JSON.parse(window.localStorage.getItem('currentDoctorServices'));
					newState.fields = JSON.parse(window.localStorage.getItem('currentDoctorFields'));
				} catch (e) {
					newState.item.billing.services = [];
					newState.fields = null;
				}

				setState(newState);
			}
		}
		
		setupComponent();
	}, []);

	const handleError = (e) => {
		console.log(e);
		setState({ ...state, error: e });
	}
	
	const handleChangeView = (currentView) => setState({ ...state, currentView });
	
	const handleSplit = async (e) => {
		e.preventDefault();

		if (window.confirm('Are you sure you want to split this appointment in half?')) {
			const app = await splitAppointment(window.localStorage.getItem('currentDoctorId'), state.item.creationTimestamp);

			if (app instanceof Error)
				handleError(app);
			else
				navigate("/");
		}
	}
	
	const handleDelete = async (e) => {
		e.preventDefault();

		if (window.confirm('Are you sure you want to delete this appointment?')) {
			const app = await deleteAppointment(window.localStorage.getItem('currentDoctorId'), state.item.creationTimestamp);

			if (app instanceof Error)
				handleError(app);
			else
				navigate("/");
		}
	}
	
	const handleCreate = async (e) => {
		e.preventDefault();

		// Store original type for user feedback
		const originalType = state.item.type;
		
		// Show loading state or disable button to prevent double submission
		setState(prevState => ({ ...prevState, isSubmitting: true }));

		const app = await createAppointment(window.localStorage.getItem('currentDoctorId'), state.item);

		if (app instanceof Error) {
			handleError(app);
			setState(prevState => ({ ...prevState, isSubmitting: false }));
		} else {
			// Provide feedback based on the type that was selected
			if (originalType === 'both') {
				alert('Appointment created successfully as both available and pending');
			} else if (originalType === 'pending') {
				alert('Appointment added to pending list');
			}
			navigate("/");
		}
	}
	
	const handleUpdate = async (e) => {
		e.preventDefault();

		const app = await updateAppointment(window.localStorage.getItem('currentDoctorId'), state.item.creationTimestamp, state.item);

		if (app instanceof Error)
			handleError(app);
		else
			navigate("/");
	}
	
	const handleChange = (e, i) => {
		if (e && e.target && e.target.id) {
			const field = e.target.id;
			const value = e.target.value;

			const item = { ...state.item };
			item.billing.services[i][field] = value;
			setState({ ...state, item });
		} else return;
	}
	
	const handleCheckChange = (e, i) => {
		if (e && e.target && e.target.id && e.target.value !== null) {
			const field = e.target.id;
			const value = e.target.checked;

			const item = { ...state.item };
			item.billing.services[i][field] = value;
			setState({ ...state, item });
		} else return;
	}
	
	const isDisabled = (name) => {
		const fields = state.fields;
		if (fields) {
			const field = fields.find((f) => f.fieldName === name);
			if (field && (field.visible === true || field.visible === "true")) return false;
			else return true;
		}

		return false;
	}
	
	const isRequired = (name) => {
		const fields = state.fields;
		if (fields) {
			const field = fields.find((f) => f.fieldName === name);
			if (field && (field.required === true || field.required === "true")) {
				if (field.visible === false || field.visible === "false") return false;
				else return true;
			}
			else return false;
		}

		return false;
	}
	
	const handleEmailChange = (e) => {
		const remindManually = (e.target.value || access(state, 'item.patient.mobile.mobileNumber')) ? "false" : "true";
		setState({ ...state, item: { ...state.item, patient: { ...state.item.patient, email: e.target.value, remindManually } } });
	}
	
	// This is a simpler and more reliable implementation to handle mobile numbers
	const handleMobileChange = (e) => {
		// Get just the entered digits without any formatting
		const mobileNumber = e.target.value.replace(/[^0-9]/g, '');
		
		// Update remindManually based on whether email or mobile exists
		const remindManually = (mobileNumber || access(state, 'item.patient.email')) ? "false" : "true";
		
		setState({ 
			...state, 
			item: { 
				...state.item, 
				patient: { 
					...state.item.patient, 
					remindManually, 
					mobile: { 
						...state.item.patient.mobile,
						mobileNumber: mobileNumber // Store ONLY the digits
					} 
				} 
			} 
		});
	}
	
	const handleCountryCodeChange = (e) => {
		setState({
			...state,
			item: {
				...state.item,
				patient: {
					...state.item.patient,
					mobile: {
						...state.item.patient.mobile,
						countryPrefixCode: e.target.value
					}
				}
			}
		});
	}

	const getCountryCode = () => {
		// Default to Malta (356) if no country code is set
		return state?.item?.patient?.mobile?.countryPrefixCode || "356";
	}

	const { editMode } = state;
	
	return (
		<div>
			<div className="pages__back-button--container">
				<FontAwesomeIcon className="pages__back-button--icon" icon="chevron-left"></FontAwesomeIcon>
				<Link className="pages__back-button--text" to="/">Back</Link>
			</div>
			<div className="tiny-push"></div>
			<div style={{ marginBottom: 0, paddingBottom: 0 }} className="pages__modal--container">
				<nav className="appointment__nav--container">
					<button onClick={() => handleChangeView('general')} id="general-info-button">GENERAL INFO</button>
					<button onClick={() => handleChangeView('remarks')}>REMARKS
					{
							state.remarksAmount !== 0 ?
								<p style={state.billingAmount ? { display: false } : { display: 'none' }} >{state.remarksAmount ? state.remarksAmount : undefined}</p>
								: undefined
						}
					</button>
					<button onClick={() => handleChangeView('billing')}>BILLING
						{
							state.remarksAmount !== 0 ?
								<p style={state.billingAmount ? { display: false } : { display: 'none' }} > {state.billingAmount ? state.billingAmount : undefined}</p>
								: undefined
						}
					</button>
				</nav>
				<div className={editMode === true ? "small-push" : "tiny-push"}></div>
				<form autoComplete='on' onSubmit={editMode === true ? handleUpdate : handleCreate}>
					{
						state.currentView === 'general' ?
							<Container>
								<Row>
									<Col xs={{ span: 5, offset: 2 }} sm={{ span: 5, offset: 2 }} md={{ span: 5, offset: 2 }} lg={{ span: 5, offset: 2 }}>
										{
											state.editMode === false && (window.localStorage.getItem("userRights") === "admin" || window.localStorage.getItem("userRights") === "limitedadmin" || window.localStorage.getItem("userRights") === "doctor") ?
												<Link className="appointment__pending-button--container" to={`/appointments/${window.localStorage.getItem('currentDoctorId')}/pending`}>Import details from pending list</Link>
												: undefined
										}
									</Col>
								</Row>
								<Row>
									<Col xs={12} sm={8} md={8} lg={8}>{/*Patient Details*/}
										<Row>
											<Col xs={{ span: 12, offset: 1 }} sm={5} md={5} lg={5}>
												<Form.Group className="mb-3">
													<Form.Label htmlFor='firstName'>First Name<span style={{ display: isRequired("firstname") ? 'inline' : 'none' }}>&nbsp;*</span></Form.Label>
													<Form.Control
														id='firstName'
														defaultValue={access(state, "item.patient.name.firstName") ? state.item.patient.name.firstName : undefined}
														required={isRequired("firstname")}
														disabled={isDisabled("firstname")}
														onChange={(e) => setState({ ...state, item: { ...state.item, patient: { ...state.item.patient, name: { ...state.item.patient.name, firstName: e.target.value } } } })}
														type='text'>
													</Form.Control>
												</Form.Group>
											</Col>
											<Col xs={{ span: 12, offset: 1 }} sm={{ span: 5, offset: 1 }} md={{ span: 5, offset: 1 }} lg={{ span: 5, offset: 1 }}>
												<Form.Group className="mb-3">
													<Form.Label htmlFor='lastName'>Surname<span style={{ display: isRequired("lastname") ? 'inline' : 'none' }}>&nbsp;*</span></Form.Label>
													<Form.Control
														id='lastName'
														defaultValue={access(state, "item.patient.name.lastName") ? state.item.patient.name.lastName : undefined}
														required={isRequired("lastname")}
														disabled={isDisabled("lastname")}
														onChange={(e) => setState({ ...state, item: { ...state.item, patient: { ...state.item.patient, name: { ...state.item.patient.name, lastName: e.target.value } } } })}
														type='text'>
													</Form.Control>
												</Form.Group>
											</Col>
										</Row>
										<Row>
											<Col xs={{ span: 12, offset: 1 }} sm={5} md={5} lg={5}>
												<Form.Group className="mb-3">
													<Form.Label htmlFor='idCard'>ID Card<span style={{ display: isRequired("idcard") ? 'inline' : 'none' }}>&nbsp;*</span></Form.Label>
													<Form.Control
														id='idCard'
														defaultValue={access(state, "item.patient.idCard") ? state.item.patient.idCard : undefined}
														required={isRequired("idcard")}
														disabled={isDisabled("idcard")}
														onChange={(e) => setState({ ...state, item: { ...state.item, patient: { ...state.item.patient, idCard: e.target.value } } })}
														type='text'>
													</Form.Control>
												</Form.Group>
											</Col>
											<Col xs={{ span: 12, offset: 1 }} sm={{ span: 5, offset: 1 }} md={{ span: 5, offset: 1 }} lg={{ span: 5, offset: 1 }}>
												<Form.Group className="mb-3">
													<Form.Label htmlFor='mobileNumber'>Mobile Number<span style={{ display: isRequired("mobilenumber") ? 'inline' : 'none' }}>&nbsp;*</span></Form.Label>
													<div style={{ display: 'flex', alignItems: 'center' }}>
														<div style={{ width: '100px', marginRight: '10px' }}>
															<Form.Control
																as="select"
																id='countryPrefixCode'
																value={getCountryCode()}
																onChange={handleCountryCodeChange}
																disabled={isDisabled("mobilenumber")}
															>
																<option value="356">+356</option>
																<option value="1">+1</option>
																<option value="7">+7</option>
																<option value="20">+20</option>
																<option value="27">+27</option>
																<option value="30">+30</option>
																<option value="31">+31</option>
																<option value="32">+32</option>
																<option value="33">+33</option>
																<option value="34">+34</option>
																<option value="36">+36</option>
																<option value="39">+39</option>
																<option value="40">+40</option>
																<option value="41">+41</option>
																<option value="43">+43</option>
																<option value="44">+44</option>
																<option value="45">+45</option>
																<option value="46">+46</option>
																<option value="47">+47</option>
																<option value="48">+48</option>
																<option value="49">+49</option>
																<option value="51">+51</option>
																<option value="52">+52</option>
																<option value="53">+53</option>
																<option value="54">+54</option>
																<option value="55">+55</option>
																<option value="56">+56</option>
																<option value="57">+57</option>
																<option value="58">+58</option>
																<option value="60">+60</option>
																<option value="61">+61</option>
																<option value="62">+62</option>
																<option value="63">+63</option>
																<option value="64">+64</option>
																<option value="65">+65</option>
																<option value="66">+66</option>
																<option value="81">+81</option>
																<option value="82">+82</option>
																<option value="84">+84</option>
																<option value="86">+86</option>
																<option value="90">+90</option>
																<option value="91">+91</option>
																<option value="92">+92</option>
																<option value="93">+93</option>
																<option value="94">+94</option>
																<option value="95">+95</option>
																<option value="98">+98</option>
																<option value="211">+211</option>
																<option value="212">+212</option>
																<option value="213">+213</option>
																<option value="216">+216</option>
																<option value="218">+218</option>
																<option value="220">+220</option>
																<option value="221">+221</option>
																<option value="222">+222</option>
																<option value="223">+223</option>
																<option value="224">+224</option>
																<option value="225">+225</option>
																<option value="226">+226</option>
																<option value="227">+227</option>
																<option value="228">+228</option>
																<option value="229">+229</option>
																<option value="230">+230</option>
																<option value="231">+231</option>
																<option value="232">+232</option>
																<option value="233">+233</option>
																<option value="234">+234</option>
																<option value="235">+235</option>
																<option value="236">+236</option>
																<option value="237">+237</option>
																<option value="238">+238</option>
																<option value="239">+239</option>
																<option value="240">+240</option>
																<option value="241">+241</option>
																<option value="242">+242</option>
																<option value="243">+243</option>
																<option value="244">+244</option>
																<option value="245">+245</option>
																<option value="248">+248</option>
																<option value="249">+249</option>
																<option value="250">+250</option>
																<option value="251">+251</option>
																<option value="252">+252</option>
																<option value="253">+253</option>
																<option value="254">+254</option>
																<option value="255">+255</option>
																<option value="256">+256</option>
																<option value="257">+257</option>
																<option value="258">+258</option>
																<option value="260">+260</option>
																<option value="261">+261</option>
																<option value="262">+262</option>
																<option value="263">+263</option>
																<option value="264">+264</option>
																<option value="265">+265</option>
																<option value="266">+266</option>
																<option value="267">+267</option>
																<option value="268">+268</option>
																<option value="269">+269</option>
																<option value="290">+290</option>
																<option value="291">+291</option>
																<option value="297">+297</option>
																<option value="298">+298</option>
																<option value="299">+299</option>
																<option value="350">+350</option>
																<option value="351">+351</option>
																<option value="352">+352</option>
																<option value="353">+353</option>
																<option value="354">+354</option>
																<option value="355">+355</option>
																<option value="357">+357</option>
																<option value="358">+358</option>
																<option value="359">+359</option>
																<option value="370">+370</option>
																<option value="371">+371</option>
																<option value="372">+372</option>
																<option value="373">+373</option>
																<option value="374">+374</option>
																<option value="375">+375</option>
																<option value="376">+376</option>
																<option value="377">+377</option>
																<option value="378">+378</option>
																<option value="380">+380</option>
																<option value="381">+381</option>
																<option value="382">+382</option>
																<option value="383">+383</option>
																<option value="385">+385</option>
																<option value="386">+386</option>
																<option value="387">+387</option>
																<option value="389">+389</option>
																<option value="420">+420</option>
																<option value="421">+421</option>
																<option value="423">+423</option>
																<option value="500">+500</option>
																<option value="501">+501</option>
																<option value="502">+502</option>
																<option value="503">+503</option>
																<option value="504">+504</option>
																<option value="505">+505</option>
																<option value="506">+506</option>
																<option value="507">+507</option>
																<option value="508">+508</option>
																<option value="509">+509</option>
																<option value="590">+590</option>
																<option value="591">+591</option>
																<option value="592">+592</option>
																<option value="593">+593</option>
																<option value="594">+594</option>
																<option value="595">+595</option>
																<option value="596">+596</option>
																<option value="597">+597</option>
																<option value="598">+598</option>
																<option value="599">+599</option>
																<option value="670">+670</option>
																<option value="672">+672</option>
																<option value="673">+673</option>
																<option value="674">+674</option>
																<option value="675">+675</option>
																<option value="676">+676</option>
																<option value="677">+677</option>
																<option value="678">+678</option>
																<option value="679">+679</option>
																<option value="680">+680</option>
																<option value="681">+681</option>
																<option value="682">+682</option>
																<option value="683">+683</option>
																<option value="685">+685</option>
																<option value="686">+686</option>
																<option value="687">+687</option>
																<option value="688">+688</option>
																<option value="689">+689</option>
																<option value="690">+690</option>
																<option value="691">+691</option>
																<option value="692">+692</option>
																<option value="850">+850</option>
																<option value="852">+852</option>
																<option value="853">+853</option>
																<option value="855">+855</option>
																<option value="856">+856</option>
																<option value="880">+880</option>
																<option value="886">+886</option>
																<option value="960">+960</option>
																<option value="961">+961</option>
																<option value="962">+962</option>
																<option value="963">+963</option>
																<option value="964">+964</option>
																<option value="965">+965</option>
																<option value="966">+966</option>
																<option value="967">+967</option>
																<option value="968">+968</option>
																<option value="970">+970</option>
																<option value="971">+971</option>
																<option value="972">+972</option>
																<option value="973">+973</option>
																<option value="974">+974</option>
																<option value="975">+975</option>
																<option value="976">+976</option>
																<option value="977">+977</option>
																<option value="992">+992</option>
																<option value="993">+993</option>
																<option value="994">+994</option>
																<option value="995">+995</option>
																<option value="996">+996</option>
																<option value="998">+998</option>
															</Form.Control>
														</div>
														<div style={{ flex: 1 }}>
															<Form.Control
																id='mobileNumber'
																value={access(state, "item.patient.mobile.mobileNumber") || ""}
																required={isRequired("mobilenumber")}
																disabled={isDisabled("mobilenumber")}
																onChange={handleMobileChange}
																type='text'
																placeholder="e.g. 79001122">
															</Form.Control>
														</div>
													</div>
												</Form.Group>
											</Col>
										</Row>
										<Row>
											<Col xs={{ span: 12, offset: 1 }} sm={5} md={5} lg={5}>
												<Form.Group className="mb-3">
													<Form.Label htmlFor='email'>Email<span style={{ display: isRequired("email") ? 'inline' : 'none' }}>&nbsp;*</span></Form.Label>
													<Form.Control
														id='email'
														defaultValue={access(state, "item.patient.email") ? state.item.patient.email : undefined}
														required={isRequired("email")}
														disabled={isDisabled("email")}
														onChange={handleEmailChange}
														type='email'>
													</Form.Control>
												</Form.Group>
											</Col>
										</Row>
										<Row>
											<Col xs={{ span: 12, offset: 1 }} sm={5} md={5} lg={5}>
												<Form.Group className="mb-3">
													<Form.Label htmlFor='landlineNumber'>Landline Number<span style={{ display: isRequired("landlinenumber") ? 'inline' : 'none' }}>&nbsp;*</span></Form.Label>
													<Form.Control
														id='landlineNumber'
														autoComplete="asdfasdfweqr"  // chrome bug --> needs to be a random string for now
														defaultValue={access(state, "item.patient.landline.landlineNumber") ? state.item.patient.landline.landlineNumber : undefined}
														required={isRequired("landlinenumber")}
														disabled={isDisabled("landlinenumber")}
														onChange={(e) => setState({ ...state, item: { ...state.item, patient: { ...state.item.patient, landline: { ...state.item.patient.landline, landlineNumber: e.target.value } } } })}
														type='tel'>
													</Form.Control>
												</Form.Group>
											</Col>
											<Col xs={{ span: 12, offset: 1 }} sm={{ span: 5, offset: 1 }} md={{ span: 5, offset: 1 }} lg={{ span: 5, offset: 1 }}>
												<Form.Group className="mb-3">
													<Form.Label htmlFor='dob'>Date of Birth<span style={{ display: isRequired("dob") ? 'inline' : 'none' }}>&nbsp;*</span></Form.Label>
													<Form.Control
														id='dob'
														defaultValue={access(state, "item.patient.dob") ? state.item.patient.dob : undefined}
														required={isRequired("dob")}
														disabled={isDisabled("dob")}
														onChange={(e) => setState({ ...state, item: { ...state.item, patient: { ...state.item.patient, dob: e.target.value } } })}
														type='text'>
													</Form.Control>
												</Form.Group>
											</Col>
										</Row>
										<Row>
											<Col xs={{ span: 12, offset: 1 }} sm={5} md={5} lg={5}>
												<Form.Group className="mb-3">
													<Form.Label htmlFor='remindManually'>Remind Patient Manually</Form.Label>
													<Form.Control
														as="select"
														id='remindManually'
														value={
															// Convert boolean to string for dropdown
															access(state, "item.patient.remindManually") !== undefined ? 
															  String(state.item.patient.remindManually) : 
															  "true"
														}
														onChange={(e) => setState({ ...state, item: { ...state.item, patient: { ...state.item.patient, remindManually: e.target.value } } })}
													>
														<option value="false" label="No"></option>
														<option value="true" label="Yes"></option>
													</Form.Control>
												</Form.Group>
											</Col>
										</Row>
										<hr></hr>
										<Row>
											<Col xs={{ span: 12, offset: 1 }} sm={7} md={7} lg={7}>
												<a target="_blank" rel="noopener noreferrer" href="https://www.google.com/maps/place/Medicaid+Pharmacy/@35.8488505,14.5055409,17z">
													<img className="appointment__map--img" src={map} alt="Medicaid Pharmacy location on Google Maps" />
												</a>
											</Col>
											<Col className="appointment__address--container" xs={{ span: 12, offset: 1 }} sm={5} md={5} lg={5}>
												<p id="business-name">Medicaid Pharmacy</p>
												<p id="business--address">62, Vjal it-Torri</p>
												<p id="business-town">Gudja</p>
												<a id="business-website" target="_blank" rel="noopener noreferrer" href="http://www.medicaidclinics.com">www.medicaidclinics.com</a><br />
												<a id="business-email" target="_blank" rel="noopener noreferrer" href="mailto:medicaidpharmacy@gmail.com">medicaidpharmacy@gmail.com</a>
												<p id="business-telephone">+356 21676294</p>
											</Col>
										</Row>
									</Col>

									<Col xs={12} sm={4} md={4} lg={4}>{/*Doc & Appointment Details*/}
										<div className="appointment__doctor-appointment--container" >
											<Row><p className="appointment__doctor--name">
												{`${camel(window.localStorage.getItem('currentDoctorTitle'))}. ${camel(window.localStorage.getItem('currentDoctorLastName'))}, ${camel(window.localStorage.getItem('currentDoctorFirstName'))}`}
											</p></Row>
											<Row><p className="appointment__doctor--speciality">{camel(window.localStorage.getItem('currentDoctorSpeciality'))}</p></Row>
											<div className="small-push"></div>
											<Row>
												<Col xs={6} sm={6} md={6} lg={6}>
													<Form.Group>
														<Form.Label htmlFor='fromTimestamp'>From</Form.Label>
														<div>
															<p id="fromTimestampDate">{moment(state.item.fromTimestamp).format('DD MMM YYYY')}</p>
															<p id="fromTimestampTime">{moment(state.item.fromTimestamp).format('HH:mm')}</p>
														</div>
													</Form.Group>
												</Col>
												<Col xs={6} sm={6} md={6} lg={6}>
													<Form.Group>
														<Form.Label htmlFor='toTimestamp'>To</Form.Label>
														<div>
															<p id="toTimestampDate">{moment(state.item.toTimestamp).format('DD MMM YYYY')}</p>
															<p id="toTimestampTime">{moment(state.item.toTimestamp).format('HH:mm')}</p>
														</div>
													</Form.Group>
												</Col>
											</Row>
											<div className="tiny-push"></div>
											<Row>
												<Col xs={6} sm={6} md={6} lg={6}>
													<Form.Group>
														<Form.Label htmlFor="clinic">Clinic</Form.Label>
														<Form.Control
															as="select"
															id='clinic'
															required
															value={state.item.clinic ? 
																// Normalize clinic name - capitalize first letter and match UI options
																state.item.clinic.charAt(0).toUpperCase() + state.item.clinic.slice(1) : 
																''}
															onChange={(e) => setState({ ...state, item: { ...state.item, clinic: e.target.value } })}>
															<option value=''>--</option>
															<option value='Clinic 1'>Clinic 1</option>
															<option value='Clinic 2'>Clinic 2</option>
															<option value='Clinic 3'>Clinic 3</option>
															<option value='Clinic 4'>Clinic 4</option>
														</Form.Control>
													</Form.Group>
												</Col>
												<Col xs={6} sm={6} md={6} lg={6}>
												</Col>
											</Row>
											<Row><p className="appointment__appointment--time-notice">* Time is approximate</p></Row>
											{
												(window.localStorage.getItem("userRights") === "admin" || window.localStorage.getItem("userRights") === "limitedadmin" || window.localStorage.getItem("userRights") === "doctor") && state.item.type !== 'filled' ?
													<Row>
														<Col xs={12} sm={12} md={12} lg={12}>
															<Form.Label htmlFor="pendingStatus">Appointment Status</Form.Label>
															<ButtonGroup className="appointment__pending-toggle">
																<Button 
																	variant={state.item.type === 'available' ? 'primary' : 'outline-primary'} 
																	onClick={() => setState({ ...state, item: { ...state.item, type: 'available' } })}
																>
																	Not Pending
																</Button>
																<Button 
																	variant={state.item.type === 'both' ? 'primary' : 'outline-primary'} 
																	onClick={() => setState({ ...state, item: { ...state.item, type: 'both' } })}
																>
																	Both
																</Button>
																<Button 
																	variant={state.item.type === 'pending' ? 'primary' : 'outline-primary'} 
																	onClick={() => setState({ ...state, item: { ...state.item, type: 'pending' } })}
																>
																	Pending
																</Button>
															</ButtonGroup>
														</Col>
													</Row>
													: null
											}
										</div>
									</Col>
								</Row>
							</Container>
							: state.currentView === 'remarks' ?
								<Container>
									<Row className="justify-content-center">
										<Col xs={12} sm={10} md={10} lg={8} xl={8}>
											<h5 className="mb-3">Pharmacy's Remarks</h5>
											<Form.Control
												as="textarea" 
												id="pharmacyRemarks"
												value={access(state, "item.pharmacyRemarks") ? state.item.pharmacyRemarks : ""}
												onChange={(e) => setState({ ...state, item: { ...state.item, pharmacyRemarks: e.target.value } })}
												rows={6}
												style={{
													width: "100%",
													minHeight: "150px",
													display: "block"
												}}
											/>
											
											<h5 className="mt-5 mb-3">Doctor's Remarks</h5>
											<Form.Control
												as="textarea"
												id="doctorRemarks"
												value={access(state, "item.doctorRemarks") ? state.item.doctorRemarks : ""}
												onChange={(e) => setState({ ...state, item: { ...state.item, doctorRemarks: e.target.value } })}
												rows={6}
												style={{
													width: "100%",
													minHeight: "150px",
													display: "block"
												}}
											/>
											
											<div className="mt-4">
												<p><strong>Email Reminder:</strong> {state.item.emailReminder}</p>
												<p><strong>SMS Reminder:</strong> {state.item.smsReminder}</p>
											</div>
										</Col>
									</Row>
								</Container>
								:
								<Container className="billing-container">
									<Row>
										<Col xs={12} md={6} className="billing-settings__services--col">
											<h3 className="centered-text">Services</h3>
											{
												access(state, "item.billing.services") ?
													state.item.billing.services.map((service, i) => {
														return (
															<fieldset className="billing-settings__form--item"
																style={
																	state.item.billing.services.length > 1 ? undefined : { border: "none" }
																}
																key={i}>
																<p className="appointment__service--name">{service.title}</p>
																<p className="appointment__service--price">€&nbsp;{service.price}</p>
																<Form.Check
																	id="bought"
																	inline
																	checked={service.bought}
																	onChange={(e) => handleCheckChange(e, i)}
																	label="Purchased?"
																/>
															</fieldset>
														)
													})
													: undefined
											}
										</Col>
										<Col xs={12} md={6}>
											<Form.Group className="mb-3">
												<Form.Label htmlFor='doctorRate'>Doctor Rate</Form.Label>
												<Form.Control
													id='doctorRate'
													defaultValue={access(state, "item.billing.doctorRate") ? state.item.billing.doctorRate : undefined}
													required
													onChange={(e) => setState({ ...state, item: { ...state.item, billing: { ...state.item.billing, doctorRate: e.target.value } } })}
													type='number'>
												</Form.Control>
											</Form.Group>
											<Form.Group className="mb-3">
												<Form.Label htmlFor='clinicFee'>Clinic Fee</Form.Label>
												<Form.Control
													id='clinicFee'
													defaultValue={access(state, "item.billing.clinicFee") ? state.item.billing.clinicFee : undefined}
													required
													onChange={(e) => setState({ ...state, item: { ...state.item, billing: { ...state.item.billing, clinicFee: e.target.value } } })}
													type='number'>
												</Form.Control>
											</Form.Group>
											<Form.Group className="mb-3">
												<Form.Label htmlFor="toBePaidAt">To be paid at</Form.Label>
												<Form.Control
													as="select"
													id='toBePaidAt'
													required
													defaultValue={state.item.billing.toBePaidAt}
													onChange={(e) => setState({ ...state, item: { ...state.item, billing: { ...state.item.billing, toBePaidAt: e.target.value } } })}>
													<option value="">--</option>
													<option value='pharmacycounter'>Pharmacy Counter</option>
													<option value='doctorinclinic'>Doctor In Clinic</option>
												</Form.Control>
											</Form.Group>
										</Col>
									</Row>
								</Container>
					}
					<Row>
						<div className="medium-push"></div>
						<div className="pages__form--error centered-text">
							{
								state.error && state.error instanceof Array ?
									state.error.map((error, i) => <p key={i}>{error.toString()}</p>)
									: <p>{state.error?.message}</p>
							}
						</div>
						{
							state.currentView === 'general' ?
								<div>
									<button
										id="save"
										className="save-button center-block medium-button"
										type='submit'> {editMode === true ? 'Update' : 'Create'}</button>
								</div>
								: false
						}
					</Row>
				</form>
			</div>
			<div style={{ marginTop: 0 }} className="pages__action-bar--container">
				{
					state.item.type === "filled" && (window.localStorage.getItem("userRights") === "admin") ?
						<div onClick={handleSplit} className="pages__action-bar--item appointment__action-bar--split">
							<FontAwesomeIcon icon="hand-scissors" />
						</div>
						: false
				}
				<div onClick={handleDelete} className="pages__action-bar--item appointment__action-bar--delete">
					<FontAwesomeIcon icon="trash-alt" />
				</div>
			</div>
			<div className="medium-push"></div>
		</div>
	);
}

export default AppointmentDetails;